/**
 * Wave 2 — Prequalification cockpit (full page / panel).
 */
import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  List,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { commercialLifecycleService } from '@/services/commercialLifecycle.service';
import { EOI_OUTCOME_OPTIONS, CLIENT_INVITATION_NOTE, epcInvitationKind } from '@/constants/epcInvitationKind';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatDate } from '@/utils/format';
import { PursuitNextActions } from './PursuitNextActions';
import { buildPursuitViewModel } from './pursuitWorkspaceModel';
import { pursuitPath } from './resolvePursuitOpportunityId';
import {
  buildPrequalReadiness,
  readinessTagColor,
  type PrequalReadinessState,
} from './prequalReadiness';
import { parsePrequalRequirements, mergeRequirementsMeta } from './prequalRequirementMatrix';
import { EpcPrequalRequirementMatrix } from './EpcPrequalRequirementMatrix';
import { EpcPrequalRequirementEditor } from './EpcPrequalRequirementEditor';
import { EpcInvitationDossierEditor } from './EpcInvitationDossierEditor';
import { mergeInvitationDossier } from '@/constants/epcInvitationWorkflow';

const { Text, Title } = Typography;

const STATUS_OPTIONS = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Qualified', label: 'Qualified' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const GATE_OPTIONS = [
  { value: 'NOT_REQUIRED', label: 'Not required' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'BLOCK', label: 'Block' },
  { value: 'PASSED', label: 'Passed' },
];

export type EpcPrequalCockpitProps = {
  row: Record<string, unknown>;
  onUpdated?: () => void;
};

export const EpcPrequalCockpit: React.FC<EpcPrequalCockpitProps> = ({ row, onUpdated }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = String(row.id || '');
  const opportunityId = String(row.opportunity_id || row.opportunityId || '');
  const isEoi = epcInvitationKind(row.invitation_kind || row.invitationKind) === 'EOI';
  const [commercialForm] = Form.useForm();

  const readiness = useMemo(() => buildPrequalReadiness(row), [row]);
  const requirementRows = useMemo(
    () => parsePrequalRequirements(row.meta_json || row.metaJson),
    [row]
  );

  const ctxQ = useQuery({
    queryKey: ['commercial-lifecycle-context', 'opportunity', opportunityId],
    queryFn: () => commercialLifecycleService.getContext('opportunity', opportunityId),
    enabled: Boolean(opportunityId),
    staleTime: 30_000,
    retry: false,
  });

  const patchM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcCommercialService.patchPrequalification(id, payload),
    onSuccess: () => {
      message.success('Prequalification updated');
      qc.invalidateQueries({ queryKey: ['epc-prequalifications'] });
      qc.invalidateQueries({ queryKey: ['epc-prequalification', id] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const nextActions = useMemo(() => {
    if (!ctxQ.data) return [];
    return buildPursuitViewModel({
      opportunityId: opportunityId || '—',
      opportunity: null,
      context: ctxQ.data,
      definition: undefined,
      tenders: [],
      contracts: [],
    }).nextActions.filter(
      (a) =>
        a.toStageKey.includes('PREQUAL') ||
        a.toStageKey.includes('TENDER') ||
        a.toStageKey.includes('OPPORTUNITY')
    );
  }, [ctxQ.data, opportunityId]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Space wrap style={{ marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0 }}>
            {String(row.prequal_number || 'Prequal')}
          </Title>
          <Tag color={readinessTagColor(readiness.overall)}>{readiness.overall}</Tag>
          <Tag>{String(row.status || '—')}</Tag>
          <Tag
            color={
              String(row.tender_gate) === 'BLOCK'
                ? 'red'
                : String(row.tender_gate) === 'WARNING'
                  ? 'orange'
                  : 'default'
            }
          >
            {String(row.tender_gate || '—')}
          </Tag>
        </Space>
        <Text type="secondary">{String(row.title || '')}</Text>
      </div>

      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Client">{String(row.client_name || '—')}</Descriptions.Item>
        <Descriptions.Item label="Due">
          {row.due_date ? formatDate(String(row.due_date)) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Opportunity">
          {opportunityId ? (
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(pursuitPath(opportunityId))}>
              Open pursuit
            </Button>
          ) : (
            '—'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Card size="small" title="Readiness groups">
        <List
          size="small"
          dataSource={readiness.groups}
          renderItem={(g) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{g.name}</Text>
                    <Tag color={readinessTagColor(g.state as PrequalReadinessState)}>{g.state}</Tag>
                  </Space>
                }
                description={
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {g.checks.map((c) => (
                      <li key={c.id}>
                        <Text>
                          {c.label}: {c.state}
                          {c.detail ? ` — ${c.detail}` : ''}
                        </Text>
                      </li>
                    ))}
                  </ul>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      {requirementRows.length > 0 && (
        <Card size="small" title="Requirement matrix">
          <EpcPrequalRequirementMatrix
            rows={requirementRows}
            submissionBlocked={readiness.overall === 'BLOCKED'}
            submissionBlockReason={
              readiness.overall === 'BLOCKED' ? 'Prequalification readiness blocked' : undefined
            }
          />
        </Card>
      )}

      <Card size="small" title="Requirements (meta_json)">
        <EpcPrequalRequirementEditor
          rows={requirementRows}
          saving={patchM.isPending}
          onSave={(requirements) => {
            const meta = (row.meta_json || row.metaJson || {}) as Record<string, unknown>;
            patchM.mutate({ metaJson: mergeRequirementsMeta(meta, requirements) });
          }}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Completion % excludes N/A rows. Submission readiness requires zero critical missing items.
        </Text>
      </Card>

      {isEoi ? (
        <Card size="small" title="Expression of Interest">
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={CLIENT_INVITATION_NOTE} />
          {String(row.eoi_outcome || row.status) !== 'Shortlisted' ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="This Expression of Interest is not shortlisted. You can still continue to a Request for Quotation or Request for Tender."
            />
          ) : null}
          <Form
            form={commercialForm}
            layout="vertical"
            initialValues={{
              clientDocumentNo: row.client_document_no,
              timezone: row.timezone,
              submissionChannel: row.submission_channel,
              confidentiality: row.confidentiality,
              scopeSummary: row.scope_summary,
              siteLocation: row.site_location,
              clientContactName: row.client_contact_name,
              eligibilitySummary: row.eligibility_summary,
              consortiumIntent: row.consortium_intent || 'None',
              conflictOfInterestDeclared: Boolean(row.conflict_of_interest_declared),
            }}
            onFinish={(values) => patchM.mutate(values)}
          >
            <Form.Item name="clientDocumentNo" label="Client document number">
              <Input />
            </Form.Item>
            <Form.Item name="timezone" label="Closing timezone">
              <Input placeholder="Asia/Dubai" />
            </Form.Item>
            <Form.Item name="submissionChannel" label="Submission channel">
              <Select
                allowClear
                options={['Portal', 'Email', 'Hard copy', 'Mixed'].map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </Form.Item>
            <Form.Item name="confidentiality" label="Confidentiality">
              <Select
                allowClear
                options={['Public', 'Confidential', 'Restricted'].map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </Form.Item>
            <Form.Item name="siteLocation" label="Site / location">
              <Input />
            </Form.Item>
            <Form.Item name="clientContactName" label="Client contact">
              <Input />
            </Form.Item>
            <Form.Item name="scopeSummary" label="Scope summary">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="eligibilitySummary" label="Eligibility summary">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="consortiumIntent" label="Consortium or JV intent">
              <Select
                options={['None', 'JV', 'Consortium', 'Subcontract'].map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="conflictOfInterestDeclared"
              label="Conflict of interest acknowledged"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={patchM.isPending}>
              Save invitation
            </Button>
          </Form>
          <Space style={{ marginTop: 12 }} wrap>
            <Button
              onClick={() => navigate(`/epc/commercial/prequalifications/${encodeURIComponent(id)}/continue`)}
            >
              Continue as RFQ or RFT
            </Button>
          </Space>
          {row.predecessor_id ? (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Predecessor {String(row.predecessor_entity || '')} {String(row.predecessor_id)}
            </Text>
          ) : null}
        </Card>
      ) : null}

      <Card size="small" title="Invitation dossier">
        <EpcInvitationDossierEditor
          kind={isEoi ? 'EOI' : 'PQ'}
          meta={row.meta_json || row.metaJson}
          saving={patchM.isPending}
          onSave={(dossier) =>
            patchM.mutate({
              metaJson: mergeInvitationDossier(row.meta_json || row.metaJson, dossier),
            })
          }
        />
      </Card>

      <Card size="small" title="Update (domain API)">
        <Space wrap>
          <Select
            style={{ minWidth: 140 }}
            value={String((isEoi ? row.eoi_outcome || row.status : row.status) || 'Draft')}
            options={
              isEoi
                ? EOI_OUTCOME_OPTIONS.map((value) => ({ value, label: value }))
                : STATUS_OPTIONS
            }
            loading={patchM.isPending}
            onChange={(status) => patchM.mutate({ status })}
            aria-label="Prequal status"
          />
          <Select
            style={{ minWidth: 160 }}
            value={String(row.tender_gate || 'NOT_REQUIRED')}
            options={GATE_OPTIONS}
            loading={patchM.isPending}
            onChange={(tenderGate) => patchM.mutate({ tenderGate })}
            aria-label="Tender gate"
          />
        </Space>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Status and tender gate write through existing prequalification APIs — no FE-only terminal
          mutation.
        </Text>
      </Card>

      <Card size="small" title="ECLE next actions">
        {!opportunityId ? (
          <Alert
            type="info"
            showIcon
            message="Link an opportunity to see ECLE actions for this pursuit."
          />
        ) : ctxQ.isError ? (
          <Alert type="warning" showIcon message="Lifecycle context unavailable" />
        ) : (
          <PursuitNextActions
            actions={nextActions}
            onSelect={(a) => {
              if (a.toStageKey.includes('TENDER')) {
                navigate('/epc/commercial/tenders');
                return;
              }
              navigate(pursuitPath(opportunityId));
            }}
          />
        )}
      </Card>
    </Space>
  );
};

export default EpcPrequalCockpit;
